import { useLazyQuery } from '@apollo/client/react';

import {
  AUDIO_CAPTURE_BUFFER_MS,
  useProjectResponseModeContext,
} from '@/shared-survey';
import {
  FORMAT_TRANSCRIPT_ANSWER,
  QuestionType,
  type GetProjectQuery,
} from '@/survey-graphql';

import type { SurveyAiFormsAudio } from './survey-ai-forms-audio.hooks';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface UseSurveyAiFormsSubmissionAudioProps {
  audio: SurveyAiFormsAudio;
  isPreviewMode: boolean;
}

export function useSurveyAiFormsSubmissionAudio({
  audio,
  isPreviewMode,
}: UseSurveyAiFormsSubmissionAudioProps) {
  const [formatTranscriptAnswer] = useLazyQuery(FORMAT_TRANSCRIPT_ANSWER, {
    errorPolicy: 'all',
  });

  const {
    responseMode,
    startDateOfTranscript,
    stopListening,
    setCurrentTranscript,
    startListening,
    currentTranscript,
  } = useProjectResponseModeContext();

  const {
    resetAudioRecord,
    createMessageAudioAsset,
    isUploadingAudio,
    lockAudioRecord,
    unlockAudioRecord,
  } = audio;

  const stopListeningAndPrepareAudio = () => {
    const wasInVoiceMode = responseMode === 'voice';
    const startDate = startDateOfTranscript();

    lockAudioRecord();
    void stopListening();

    return { wasInVoiceMode, startDate };
  };

  const formatTranscriptAndCreateAudioAsset = async (
    wasInVoiceMode: boolean,
    startDate: number | null,
    question: ProjectQuestion | undefined,
    transcript: string | undefined
  ) => {
    let formattedTranscript = transcript;

    const questionType = question?.type;
    if (
      wasInVoiceMode &&
      currentTranscript &&
      questionType &&
      questionType === QuestionType.OpenEnded
    ) {
      const { data } = await formatTranscriptAnswer({
        variables: {
          input: {
            questionType,
            options: question?.options?.map((opt) => ({
              value: opt.value,
              label: opt.label ?? opt.value,
            })),
            transcript: transcript ?? currentTranscript,
          },
        },
      });

      const formatted = data?.formattedTranscriptAnswer?.formattedTranscript;
      if (formatted) {
        formattedTranscript = formatted;
      }
    }

    const audioAsset =
      wasInVoiceMode && !isPreviewMode
        ? await createMessageAudioAsset(
            startDate ? startDate - AUDIO_CAPTURE_BUFFER_MS : undefined
          )
        : undefined;

    return {
      assets: audioAsset ? [audioAsset] : undefined,
      formattedTranscript,
    };
  };

  const cleanupAudioAfterSubmit = (wasInVoiceMode: boolean) => {
    setCurrentTranscript('');
    unlockAudioRecord();
    resetAudioRecord();

    if (wasInVoiceMode) {
      void startListening();
    }
  };

  return {
    isUploadingAudio,
    stopListeningAndPrepareAudio,
    formatTranscriptAndCreateAudioAsset,
    cleanupAudioAfterSubmit,
  };
}
